import moment from "moment";
import {
    getCategoricalColor,
    getCategoricalNeutral,
} from "../../../utils/ColorUtils";
import {
    describeGroupKey,
    formatDateUnitKey,
    MAX_RULE_GROUPS,
} from "./asyncTableGroupRules";
import {formatGroupValue} from "./asyncTableGrouping";

// "Każda wartość" - podgląd pokazuje tyle pierwszych grup z odpowiedzi (API zwraca 20),
// reszta jako "… i N kolejnych"
export const PREVIEW_GROUPS_LIMIT = 10;

// do tylu grup nadrzędnych przełącznik nad wykresem to chipy, więcej - lista wyboru
export const PARENT_CHIPS_LIMIT = 6;

// ostrzeżenia podglądu (Preview.Warnings) - nieznane kody pomijamy
const PREVIEW_WARNING_KEYS = {
    EMPTY_GROUP: "groupWarningEmptyGroup",
    OVERLAP: "groupWarningOverlap",
    SMALL_GROUP: "groupWarningSmallGroup",
    DOMINANT: "groupWarningDominant",
    REST_EMPTY: "groupWarningRestEmpty",
    PARENT_UNEVEN: "groupWarningParentUneven",
    SINGLETON_SUBGROUPS: "groupWarningSingletonSubgroups",
};

// ostrzeżenia kolumny (Profile.ColumnWarnings)
const COLUMN_WARNING_KEYS = {
    NO_ROWS: "groupWarningNoRows",
    ALL_NULL: "groupWarningAllNull",
    SINGLE_VALUE: "groupWarningSingleValue",
    UNIQUE: "groupWarningUnique",
};

const DATE_UNIT_SUGGESTION_KEYS = {
    day: "groupSuggestionByDay",
    week: "groupSuggestionByWeek",
    month: "groupSuggestionByMonth",
};

/**
 * Liczba wg locale.
 * @param {number|string} value
 * @param {string} [locale]
 */
export function formatNumber(value, locale) {
    const number = Number(value);
    if (value === null || value === "" || isNaN(number)) return String(value);
    return new Intl.NumberFormat(locale, {maximumFractionDigits: 2}).format(
        number
    );
}

// daty z API jako "YYYY-MM-DD" (albo ISO w UTC)
const toDate = (value) => moment.utc(value);

/**
 * Liczba z danych jak w kolumnie - formatter nagłówka (np. waga w jednostce użytkownika),
 * bez formattera wg locale.
 * @param {number} value
 * @param {Object} [column] nagłówek kolumny
 * @param {string} [locale]
 */
function formatColumnNumber(value, column, locale) {
    if (column?.valueFormatter) {
        const formatted = formatGroupValue(value, column);
        if (formatted !== null && formatted !== undefined && formatted !== "") {
            return String(formatted);
        }
    }
    return formatNumber(value, locale);
}

/**
 * Wartość z `Profile.Column` (min / max) - liczba albo data.
 * @param {*} value
 * @param {string} [locale]
 * @param {Object} [column] nagłówek kolumny - liczby jak w kolumnie
 */
export function formatColumnValue(value, locale, column) {
    if (value === null || value === undefined) return "";
    if (typeof value === "number") {
        return formatColumnNumber(value, column, locale);
    }
    const date = toDate(value);
    return date.isValid() ? date.format("L") : String(value);
}

/**
 * Etykieta słupka na osi - `label.type` służy tylko do sformatowania.
 * @param {Object} label BarLabel z API
 * @param {Object} column nagłówek kolumny (formatter wartości)
 * @param {function} t tłumaczenia
 * @param {string} [locale]
 * @returns {string}
 */
export function formatBarLabel(label, column, t, locale) {
    switch (label?.type) {
        case "value":
            return String(
                formatGroupValue(label.value, column) ?? t("emptyGroupValue")
            );
        case "range":
            return `${formatColumnNumber(label.from, column, locale)}–${formatColumnNumber(label.to, column, locale)}`;
        case "period": {
            const from = toDate(label.from);
            if (label.unit === "month") return from.format("MMM YYYY");
            if (label.unit === "week") {
                return `${from.format("DD.MM")}–${toDate(label.to).format("DD.MM")}`;
            }
            return from.format("L");
        }
        case "below":
            return `< ${formatColumnNumber(label.to, column, locale)}`;
        case "above":
            return `≥ ${formatColumnNumber(label.from, column, locale)}`;
        case "other":
            return t("costsView.transactionTypes.other");
        default:
            return "";
    }
}

/**
 * Etykieta grupy podglądu ("Każda wartość", grupy nadrzędne) - jak etykieta grupy w tabeli.
 * @param {Object} label GroupLabel z API
 * @param {Object} column nagłówek kolumny poziomu
 * @param {function} t
 * @returns {string}
 */
export function formatGroupLabel(label, column, t) {
    switch (label?.type) {
        case "expression":
            return label.text;
        case "value":
            return String(
                formatGroupValue(label.value, column) ?? t("emptyGroupValue")
            );
        case "period":
            return (
                formatDateUnitKey(label.from, label.unit, t) ??
                toDate(label.from).format("L")
            );
        case "rest":
            return label.text
                ? `${t("grid.rest")} (${label.text})`
                : t("grid.rest");
        case "null":
            return t("emptyGroupValue");
        default:
            return "";
    }
}

/**
 * Etykieta grupy poziomu wyżej. Poziom z regułą - jak w tabeli: wyrażenie wpisane przez użytkownika
 * (w jednostce kolumny) z nazwą grupy; serwer zna tylko przedziały w jednostce danych.
 * @param {{key: *, label: Object}} group grupa z `Profile.ParentGroups`
 * @param {Object} [parentColumn] nagłówek kolumny poziomu wyżej
 * @param {Object} [parentRule] reguła poziomu wyżej (gdy grupuje "Własnymi grupami")
 * @param {function} t
 * @returns {string}
 */
export function formatParentGroupLabel(group, parentColumn, parentRule, t) {
    const described =
        parentRule &&
        describeGroupKey(parentRule, parentColumn?.filterType, group.key);
    if (!described) return formatGroupLabel(group.label, parentColumn, t);
    const label =
        described.kind === "bucket"
            ? described.label
            : t(described.kind === "rest" ? "grid.rest" : "emptyGroupValue");
    return described.name ? `${label} · ${described.name}` : label;
}

/**
 * Kolor grupy wyrażenia po kluczu (indeks przedziału): przedziały z palety kategorii,
 * "Pozostałe" i wiersze poza grupami (klucz N) oraz "(brak)" szare - jak na liście grup.
 * @param {number|string} key
 * @param {number} bucketsCount N - liczba przedziałów
 */
export function getGroupKeyColor(key, bucketsCount) {
    const index = Number(key);
    if (index >= 0 && index < bucketsCount) return getCategoricalColor(index);
    return getCategoricalNeutral();
}

/**
 * Segmenty słupka wykresu (od dołu) z kolorem.
 * @param {Object} bar słupek z `Profile.Bars`
 * @param {Object} options
 * @param {"groups"|"parents"|"none"} options.colorBy grupy wyrażenia (`BarGroups`), grupy nadrzędne (`split`)
 *        albo jeden kolor
 * @param {Object} [options.barGroups] `Preview.BarGroups[i]` tego słupka
 * @param {number} [options.bucketsCount] liczba przedziałów wyrażenia
 * @param {Array} [options.parentGroups] `Profile.ParentGroups` - kolejność kolorów grup nadrzędnych
 * @returns {Array<{key: string, count: number, color: string}>}
 */
export function getBarSegments(
    bar,
    {colorBy, barGroups, bucketsCount, parentGroups}
) {
    if (colorBy === "groups" && barGroups) {
        return Object.keys(barGroups)
            .sort((a, b) => Number(a) - Number(b))
            .map((key) => ({
                key,
                count: barGroups[key],
                color: getGroupKeyColor(key, bucketsCount),
            }));
    }
    if (colorBy === "parents" && bar.split) {
        return (parentGroups ?? [])
            .map((group, index) => ({
                key: String(group.key),
                count: bar.split[String(group.key)] ?? 0,
                color: getCategoricalColor(index) ?? getCategoricalNeutral(),
            }))
            .filter((segment) => segment.count > 0);
    }
    return [{key: "all", count: bar.count, color: getCategoricalNeutral()}];
}

/**
 * Dane wykresu rozkładu dla recharts: wiersz na słupek z liczbą wierszy każdego segmentu
 * i seria (`Bar` w stosie) na segment - grupę wyrażenia, grupę nadrzędną albo jeden kolor.
 * @param {Array} bars `Profile.Bars`
 * @param {Array<string>} labels etykiety słupków na osi
 * @param {Object} options jak w `getBarSegments`, `barGroups` - całe `Preview.BarGroups`
 * @returns {{rows: Array<Object>, series: Array<{key: string, dataKey: string, color: string}>}}
 */
export function buildChartData(bars, labels, options) {
    const series = [];
    const dataKeys = {};
    const rows = bars.map((bar, index) => {
        const row = {
            label: labels[index],
            count: bar.count,
            partial: !!bar.partial,
            edge: !!bar.edge,
        };
        getBarSegments(bar, {
            ...options,
            barGroups: options.barGroups?.[index],
        }).forEach((segment) => {
            if (!dataKeys[segment.key]) {
                dataKeys[segment.key] = `segment${series.length}`;
                series.push({
                    key: segment.key,
                    dataKey: dataKeys[segment.key],
                    color: segment.color,
                });
            }
            row[dataKeys[segment.key]] = segment.count;
        });
        return row;
    });
    // kolejność w stosie jak kolejność grup (od dołu)
    if (options.colorBy === "groups") {
        series.sort((a, b) => Number(a.key) - Number(b.key));
    }
    return {rows, series};
}

/**
 * Treść ostrzeżenia podglądu. Etykiety grup z modala (`labelsByKey`), grup nadrzędnych z profilu.
 * @param {{code: string, key?: *, parentKey?: *, value?: number}} warning
 * @param {Object} options
 * @param {function(*): string} options.groupLabel etykieta grupy po kluczu
 * @param {function(*): string} options.parentLabel etykieta grupy nadrzędnej po kluczu
 * @param {string} [options.childName] nazwa kolumny poziomu niżej
 * @param {function} t
 * @returns {string|null} null - nieznany kod
 */
export function formatPreviewWarning(
    warning,
    {groupLabel, parentLabel, childName},
    t
) {
    const key = PREVIEW_WARNING_KEYS[warning.code];
    if (!key) return null;
    return t(key, {
        label: warning.key !== undefined ? groupLabel(warning.key) : "",
        parentLabel:
            warning.parentKey !== undefined
                ? parentLabel(warning.parentKey)
                : "",
        count: warning.value ?? 0,
        name: childName ?? "",
    });
}

// ostrzeżenia kolumny, które dotyczą tylko grupowania po surowych wartościach
const RAW_VALUES_COLUMN_WARNINGS = ["UNIQUE"];

/**
 * Treść ostrzeżenia kolumny. Profil kolumny nie zależy od konfiguracji poziomu, więc ostrzeżenie
 * o unikalnych wartościach pomijamy, gdy poziom ich nie grupuje (własne grupy, okres daty).
 * @param {string} code
 * @param {function} t
 * @param {boolean} [rawValues] poziom grupuje po każdej surowej wartości
 * @returns {string|null}
 */
export function formatColumnWarning(code, t, rawValues = true) {
    if (!rawValues && RAW_VALUES_COLUMN_WARNINGS.includes(code)) return null;
    const key = COLUMN_WARNING_KEYS[code];
    return key ? t(key) : null;
}

/**
 * Nazwa chipa propozycji.
 * @param {{code: string, n?: number, unit?: string, length?: number}} label SuggestionLabel z API
 * @returns {string|null}
 */
export function formatSuggestionLabel(label, t) {
    switch (label?.code) {
        case "EQUAL_GROUPS":
            return t("groupSuggestionEqualGroups", {count: label.n});
        case "BY_DATE_UNIT":
            return DATE_UNIT_SUGGESTION_KEYS[label.unit]
                ? t(DATE_UNIT_SUGGESTION_KEYS[label.unit])
                : null;
        case "EACH_VALUE":
            return t("allValues");
        case "TEXT_TOKENS":
            return t("groupSuggestionTextTokens");
        case "TEXT_PREFIX":
            return t("groupSuggestionTextPrefix", {count: label.length});
        default:
            return null;
    }
}

/**
 * Propozycje, które modal umie zastosować: przedziały ponad limit grup pomijamy
 * (wyrażenia nie dałoby się zastosować), nieznane rodzaje i etykiety też.
 * @param {Array} suggestions `Profile.Suggestions`
 * @param {function} t
 * @returns {Array<{suggestion: Object, label: string}>}
 */
export function getUsableSuggestions(suggestions, t) {
    return (suggestions ?? [])
        .filter((suggestion) =>
            suggestion.kind === "buckets"
                ? suggestion.buckets?.length > 0 &&
                  suggestion.buckets.length <= MAX_RULE_GROUPS
                : ["dateUnit", "values"].includes(suggestion.kind)
        )
        .map((suggestion) => ({
            suggestion,
            label: formatSuggestionLabel(suggestion.label, t),
        }))
        .filter((item) => item.label);
}
